import { Injectable } from '@angular/core';
import { RodeoEvent } from 'model/rodeoEvent';
import { ApiService } from './api';
import { ReportPrintOptions } from 'model/load-card-print-options';
import { TurnoutReportData } from 'model/turnout-report.model';
import { ContestantPaidItemReport } from 'model/contestant-paid-item-report.model';

@Injectable()
export class ReportsService {
    constructor(private api: ApiService) { }

    getFullEntryReport(rodeoId: number, options: any[], searchName: string, selectedRodeoEventId: number) {
        return new Promise((resolve, reject) => {
            this.api
                .getFileContentsAuth(this.api.endpoints.reports + '/getFullEntryReport?rodeoId=' + rodeoId + '&options=' + options + '&searchName=' + searchName + '&selectedRodeoEventId=' + selectedRodeoEventId)
                .then(data => resolve(data.data), error => reject(error));
        });
    }

    public async getTurnoutReport(options: ReportPrintOptions) {
      return this.api.postWithAuth<TurnoutReportData>(`${this.api.endpoints.reports}/GetTurnoutReport`, options);
    }

    public async getTurnoutCsv(options: ReportPrintOptions) {
      const file = await this.api.getFileContentsAuth(`${this.api.endpoints.reports}/GetTurnoutReportCsv`, options);
      this.saveAsFile(file.data, file.filename, file.contentType);
    }

    public async getLoadCardsPdf(options: ReportPrintOptions) {
      const pdf = await this.api.getFileContentsAuth(`${this.api.endpoints.reports}/GetLoadCards`, options);
      this.saveAsFile(pdf.data, pdf.filename || 'Load Cards', pdf.contentType);
    } 

    public async getLongFormPdf(options: ReportPrintOptions) {
      const file = await this.api.getFileContentsAuth(`${this.api.endpoints.reports}/GetLongFormReport`, options);
      this.saveAsFile(file.data, file.filename, file.contentType);
    }

    public async getReservationReport(options: ReportPrintOptions, type: 'stalls' | 'rvs,', mode: 'pdf' | 'csv') {
      const file = await this.api.getFileContentsAuth(`${this.api.endpoints.reports}/GetReservationReport?type=${type}&mode=${mode}`, options);
      this.saveAsFile(file.data, file.filename, file.contentType);
    }

    public async getDaySheets(filetype: string, options: ReportPrintOptions) {
      const file = await this.api.getFileContentsAuth(`${this.api.endpoints.reports}/${filetype == 'pdf' ? 'GetDaySheetPdf' : 'GetDaySheetsCsv'}`, options);
      this.saveAsFile(file.data, file.filename, file.contentType);
    }

    public async getJudgeSheets(options: ReportPrintOptions) {
      const csv = await this.api.getFileContentsAuth(`${this.api.endpoints.reports}/getJudgeSheets`, options);
      this.saveAsFile(csv.data, csv.filename, csv.contentType);
    }

    public async getSecretarySheet(options: ReportPrintOptions) {
      const csv = await this.api.getFileContentsAuth(`${this.api.endpoints.reports}/GetSecretarySheets`, options);
      this.saveAsFile(csv.data, csv.filename, csv.contentType);
    }

    public async downloadStripePayoutCsv(reportId:string, fileName: string) {
      const report = await this.api.getFileContentsAuth(`${this.api.endpoints.reports}/DownloadStripeReport?reportId=${reportId}&fileName=${fileName}`);
      this.saveAsFile(report.data, fileName + '-StripePayoutTransactions.csv', 'text/csv');
    }

    public async getTransactionReport(rodeoId: number) {
      return this.api.getWithAuth(`${this.api.endpoints.reports}/GetTransactionsByRodeo?rodeoId=${rodeoId}`);
    }

    public async getPaymentPlans(rodeoId: number, accountId: number = 0) {
      let url = `${this.api.endpoints.reports}/GetPaymentPlans?`;
      if (rodeoId > 0) {
        url += `rodeoId=${rodeoId}`;
      }
      if (accountId > 0) {
        url += `${rodeoId > 0 ? '&' : ''}accountId=${accountId}`;
      }
      return this.api.getWithAuth(url);
    }

    public async getEquistatReport(fileName: string, rodeoEventIds: number[], winnersOnly: boolean, showPenaltyRunsOverride: boolean) {
      const report = await this.api.getFileContentsAuth(`${this.api.endpoints.reports}/GetEquiStatReport?winnersOnly=${winnersOnly}&showPenaltyRunsOverride=${showPenaltyRunsOverride}`, rodeoEventIds);
      this.saveAsFile(report.data, fileName + '- EquiStat.csv', 'text/csv');
  }
  
  public async getContestantPaidReportPdf(rodeoId: number, searchName?: string) {
    let url = `${this.api.endpoints.reports}/GetContestantPaidReportPDF?rodeoId=${rodeoId}`;

    if (searchName) {
      url += `&searchName=${searchName}`;
    }

    const pdf = await this.api.getFileContentsAuth(url, {});
    this.saveAsFile(pdf.data, pdf.filename || 'Contestat Paid Item Report', pdf.contentType);
  }

  public async getContestantPaidReportInfo(rodeoId: number, searchName?: string) {
    let url = `${this.api.endpoints.reports}/GetContestantPaidReportInfo?rodeoId=${rodeoId}`;

    if (searchName) {
      url += `&searchName=${searchName}`;
    }

    return this.api.postWithAuth<ContestantPaidItemReport>(url);
  }

    public exportToCsv(rows: object[], fileName: string, columns?: string[], skipKeysCheck: boolean = false): string {
        if (!rows || !rows.length) {
          return;
        }
        const separator = ',';
        const keys = skipKeysCheck ? columns : Object.keys(rows[0]).filter(k => {
          if (columns != null && columns.length) {
            return columns.includes(k);
          } else {
            return true;
          }
        });
        const csvContent =
          keys.join(separator) +
          '\n' +
          rows.map(row => {
            return keys.map(k => {
              let cell = row[k] === null || row[k] === undefined ? '' : row[k];
              cell = cell instanceof Date
                ? cell.toLocaleString()
                : cell.toString().replace(/"/g, '""');
              if (cell.search(/("|,|\n)/g) >= 0) {
                cell = `"${cell}"`;
              }
              return cell;
            }).join(separator);
          }).join('\n');
        
          this.saveAsFile(csvContent, `${fileName}.csv`, 'text/csv');
    }

    private saveAsFile(data: any, fileName: string, type: string): void {
        const blob = new Blob([data], { type });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(a.href);
        a.remove();
    }
}



// WEBPACK FOOTER //
// ./src/services/reports.service.ts